import { useEffect } from "react";
import config from "../config/config";
import { RestEndpoints } from "../api/endpoints";
import { decodeToken, IAuthConfig } from "../utils/auth";
import getInstance from "../utils/rest";

const { tokenKey } = config.auth as IAuthConfig;

/** Activity events cheap enough to listen for globally. */
const ACTIVITY_EVENTS: (keyof WindowEventMap)[] = ["mousedown", "keydown", "scroll"];

/**
 * Don't ping more than once per interval, however much the user moves. The window
 * is well inside the token's half-life, so a renewal is never missed.
 */
const THROTTLE_MS = 5 * 60 * 1000;

/**
 * Keeps an actively-used session alive.
 *
 * Sliding renewal is request-driven, so a user who spends an hour filling in a
 * reporting form without triggering any API call would be logged out on submit and
 * lose their work. This pings a 204 endpoint on user activity, letting the auth
 * middleware renew the token as it would for any other request.
 *
 * Only fires once the token is past its half-life — the backend refuses to renew
 * before that anyway — so a quiet tab makes no requests at all and still times out
 * as intended. The TTL is read from the token itself (`exp - iat`) rather than from
 * config, so it tracks the backend's JWT_EXPIRATION with no frontend release.
 */
export const useSessionKeepalive = (): void => {
  useEffect(() => {
    let lastPing = 0;

    const handleActivity = (): void => {
      const now = Date.now();
      if (now - lastPing < THROTTLE_MS) {
        return;
      }

      const claims = decodeToken(localStorage.getItem(tokenKey) ?? "");
      if (!claims) {
        return;
      }

      const halfLifeMs = (claims.exp - (claims.exp - claims.iat) / 2) * 1000;
      if (now < halfLifeMs) {
        return;
      }

      lastPing = now;
      // Fire and forget: a failure here is not actionable, and a 401 is already
      // handled by the shared response interceptor.
      void getInstance()
        .get(RestEndpoints.Session)
        .catch(() => undefined);
    };

    ACTIVITY_EVENTS.forEach((event) => window.addEventListener(event, handleActivity, { passive: true }));

    return () => {
      ACTIVITY_EVENTS.forEach((event) => window.removeEventListener(event, handleActivity));
    };
  }, []);
};
